import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';

const MfaLost: React.FC = () => {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Note: Using 'as any' since this endpoint might not be in OpenAPI spec yet
  const resetMutation = $api.useMutation('post', '/api/auth/mfa-reset-request' as any);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    resetMutation.mutate(
      { body: { email } },
      {
        onSuccess: () => setSuccess(true),
        onError: () => setSuccess(true), // Simulate success for security
      }
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-full">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">KYC Pro</h1>
          <p className="text-gray-600">Réinitialiser votre MFA</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {resetMutation.error && (
            <ErrorMessage error={resetMutation.error} className="mb-4" />
          )}

          {success ? (
            <div>
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
                Un email de confirmation a été envoyé à votre adresse. Cliquez sur le lien dans l'email pour confirmer la réinitialisation de votre MFA.
              </div>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                <strong>Note :</strong> Pour des raisons de sécurité, votre MFA sera désactivé 24 heures après la confirmation.
              </div>
              <Link
                to="/auth"
                className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Si vous avez perdu l'accès à votre appareil d'authentification à deux facteurs, entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre MFA.
                </p>
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
                  <strong>Important :</strong> La désactivation du MFA sera effective 24 heures après la confirmation pour des raisons de sécurité.
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="votre@email.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={resetMutation.isPending}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetMutation.isPending ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Envoi...
                  </div>
                ) : (
                  'Demander la réinitialisation MFA'
                )}
              </button>

              <div className="text-center text-sm text-gray-600">
                <Link to="/auth" className="text-blue-600 hover:text-blue-500 font-medium">
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>&copy; 2025 KYC Pro. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
};

export default MfaLost;
